// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {GenericErrorModal} from '@app/features/app/components/alerts/GenericErrorModal';
import {ChannelItem} from '@app/features/app/components/layout/ChannelItem';
import channelItemStyles from '@app/features/app/components/layout/ChannelItem.module.css';
import {ChannelItemContent} from '@app/features/app/components/layout/ChannelItemContent';
import styles from '@app/features/app/components/layout/ChannelListContent.module.css';
import {
	CollapsedCategoryVoiceParticipants,
	CollapsedChannelAvatarStack,
} from '@app/features/app/components/layout/CollapsedCategoryVoiceParticipants';
import {GenericChannelItem} from '@app/features/app/components/layout/GenericChannelItem';
import {GuildDetachedBanner} from '@app/features/app/components/layout/GuildDetachedBanner';
import {NullSpaceDropIndicator} from '@app/features/app/components/layout/NullSpaceDropIndicator';
import {ScrollIndicatorOverlay} from '@app/features/app/components/layout/ScrollIndicatorOverlay';
import {type DragItem, DragItemType, type DropResult} from '@app/features/app/components/layout/types/DndTypes';
import {
	shouldShowCategoryWhenHidingMutedChannels,
	shouldShowChannelInCollapsedCategory,
	shouldShowChannelWhenHidingMutedChannels,
} from '@app/features/app/components/layout/utils/ChannelListVisibility';
import {createChannelMoveOperation} from '@app/features/app/components/layout/utils/ChannelMoveOperation';
import {
	type ChannelGroup,
	groupChannels,
	groupChildren,
	inTreeOrder,
	isVoiceChannel,
	organizeChannels,
	subtreeChannelOrder,
} from '@app/features/app/components/layout/utils/ChannelOrganization';
import {
	type ChannelUnreadState,
	getChannelUnreadState,
} from '@app/features/app/components/layout/utils/ChannelUnreadState';
import {VoiceParticipantsList} from '@app/features/app/components/layout/VoiceParticipantsList';
import {
	type RememberedSkeletonGuildChannelGroup,
	type RememberedSkeletonGuildChannelRow,
	reportSkeletonGuildChannelList,
} from '@app/features/app/components/skeleton/SkeletonLayoutMemory';
import type {GuildBannerPresentation} from '@app/features/app/hooks/useGuildBannerPresentation';
import {useRovingFocusList} from '@app/features/app/hooks/useRovingFocusList';
import {
	measureSkeletonTextWidthPx,
	useSkeletonLayoutReport,
} from '@app/features/app/hooks/useSkeletonLayoutMemoryCapture';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import type {Guild} from '@app/features/guild/models/Guild';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import {
	asGuildPageSegment,
	getGuildChannelSegment,
	getGuildPathSegment,
} from '@app/features/navigation/utils/GuildRouteSegments';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import Permission from '@app/features/permissions/state/Permission';
import {useLocation} from '@app/features/platform/components/router/RouterReact';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import ReadStates from '@app/features/read_state/state/ReadStates';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {SidebarThreadList} from '@app/features/threads/components/SidebarThreadList';
import ThreadGuilds from '@app/features/threads/state/ThreadGuilds';
import {ChannelListContextMenu} from '@app/features/ui/action_menu/ChannelListContextMenu';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as DimensionCommands from '@app/features/ui/commands/DimensionCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import type {ScrollerHandle} from '@app/features/ui/components/Scroller';
import {Scroller} from '@app/features/ui/components/Scroller';
import {useDragAutoScroll} from '@app/features/ui/hooks/useDragAutoScroll';
import Dimension from '@app/features/ui/state/Dimension';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import * as UserGuildSettingsCommands from '@app/features/user/commands/UserGuildSettingsCommands';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {MAX_CHANNELS_PER_CATEGORY} from '@fluxer/constants/src/LimitConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {UsersThreeIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type {MotionValue} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useDragLayer} from 'react-dnd';

const CATEGORY_FULL_DESCRIPTOR = msg({
	message: 'Category full',
	comment: 'Short label in the app layout channel list content.',
});
const THIS_CATEGORY_ALREADY_CONTAINS_THE_MAXIMUM_OF_CHANNELS_DESCRIPTOR = msg({
	message: 'This category already contains the maximum of {maxChannelsPerCategory} channels.',
	comment:
		'Modal body shown when a category cannot accept another channel because the per-category limit is reached. Limit is interpolated.',
});
const CHANNELS_DESCRIPTOR = msg({
	message: '{guildName} channels',
	comment: 'Short label in the app layout channel list content. Preserve {guildName}; it is inserted by code.',
});
const CAST_DESCRIPTOR = msg({
	message: 'Cast',
	context: 'channel-list-entry',
	comment: 'Sidebar entry above the channel list opening the community cast overview page.',
});
const CAST_SELECTED_DESCRIPTOR = msg({
	message: 'Cast (selected)',
	context: 'channel-list-entry',
	comment: 'Accessible label for the sidebar Cast entry when its page is the current view.',
});
const NEW_MESSAGES_DESCRIPTOR = msg({
	message: 'New messages',
	comment: 'Short label in the app layout channel list content.',
});
const MEMBERS_PAGE_PERMISSIONS =
	Permissions.MANAGE_GUILD |
	Permissions.MANAGE_ROLES |
	Permissions.MANAGE_NICKNAMES |
	Permissions.BAN_MEMBERS |
	Permissions.MODERATE_MEMBERS |
	Permissions.KICK_MEMBERS;
const EMPTY_ARRAY: ReadonlyArray<never> = Object.freeze([]);

/**
 * One skeleton-memory entry, in DOM order. LOCAL-ONLY: a category's rows can come after a nested category now
 * (the one interleaved order), so those rows are reported as a header-less continuation entry after it; the
 * memory measures names in DOM order and must see them in the same order.
 */
interface ResolvedChannelGroup {
	readonly key: string;
	readonly category: Channel | undefined;
	readonly isCollapsed: boolean;
	/** The channel rows this entry renders, in DOM order. */
	readonly rows: ReadonlyArray<Channel>;
}

function createChannelListProjectionKey(
	groups: ReadonlyArray<ResolvedChannelGroup>,
	membersRowVisible: boolean,
): string {
	const groupKeys = groups.map((group) => {
		const channelKeys = group.rows.map((channel) => `${channel.id}~${channel.name}`);
		return `${group.category?.id ?? ''}~${group.category?.name ?? ''}~${group.isCollapsed}~${channelKeys.join(',')}`;
	});
	return `${membersRowVisible}|${groupKeys.join('|')}`;
}

function createRememberedChannelGroups(
	groups: ReadonlyArray<ResolvedChannelGroup>,
	container: HTMLElement | null,
): ReadonlyArray<RememberedSkeletonGuildChannelGroup> {
	const categoryNameElements = container?.querySelectorAll(`.${channelItemStyles.categoryName}`);
	const channelNameElements = container?.querySelectorAll(`.${channelItemStyles.channelName}`);
	let categoryNameIndex = 0;
	let channelNameIndex = 0;
	const readNextChannelWidth = (): number => {
		const width = measureSkeletonTextWidthPx(channelNameElements?.item(channelNameIndex) ?? null);
		channelNameIndex += 1;
		return width;
	};
	return groups.map((group) => {
		let categoryNameWidthPx = measureSkeletonTextWidthPx(null);
		if (group.category != null) {
			categoryNameWidthPx = measureSkeletonTextWidthPx(categoryNameElements?.item(categoryNameIndex) ?? null);
			categoryNameIndex += 1;
		}
		const channels: Array<RememberedSkeletonGuildChannelRow> = group.rows.map((channel) => ({
			voice: isVoiceChannel(channel),
			nameWidthPx: readNextChannelWidth(),
		}));
		return {
			categoryHeaderVisible: group.category != null,
			collapsed: group.isCollapsed,
			categoryNameWidthPx,
			channels,
		};
	});
}
interface ChannelListContentProps {
	readonly guild: Guild;
	readonly scrollY: MotionValue<number>;
	readonly banner: GuildBannerPresentation;
}
export const ChannelListContent = observer(({guild, scrollY, banner}: ChannelListContentProps) => {
	const threadsActive = ThreadGuilds.isActive(guild.id);
	const {i18n} = useLingui();
	const channels = Channels.getGuildChannels(guild.id);
	const location = useLocation();
	const userGuildSettings = UserGuildSettings.getSettingsForScope(guild.id);
	const isDraggingAnything = useDragLayer((monitor) => {
		if (!monitor.isDragging()) return false;
		const itemType = monitor.getItemType();
		return (
			itemType === DragItemType.CHANNEL ||
			itemType === DragItemType.CATEGORY ||
			itemType === DragItemType.VOICE_PARTICIPANT
		);
	});
	const [activeDragItem, setActiveDragItem] = useState<DragItem | null>(null);
	const scrollerRef = useRef<ScrollerHandle>(null);
	const showIntegratedBanner = banner.collapsible;
	const channelGroupsContainerRef = useRef<HTMLDivElement | null>(null);
	const stickToBottomRef = useRef(false);
	const pendingScrollTopRef = useRef<number | null>(null);
	const scrollPersistRafRef = useRef<number | null>(null);
	const channelListNavigationRef = useRovingFocusList<HTMLDivElement>({
		focusableSelector: '[data-channel-list-focus-item="true"]',

		enabled: KeyboardMode.keyboardModeEnabled,
		restoreFocusOnWindowFocus: false,
	});
	const connectedChannelId = MediaEngine.channelId;
	const hideMutedChannels = userGuildSettings?.hide_muted_channels ?? false;
	const showFadedUnreadOnMutedChannels = Accessibility.showFadedUnreadOnMutedChannels;
	// Deliberately reuses MEMBERS_PAGE_PERMISSIONS rather than inventing a cast-specific permission:
	// this entry occupies the slot the Members entry did, under the same gate.
	//
	// Not gated on platform. This list is the mobile "no channel selected" screen as well as the
	// desktop sidebar — GuildLayout renders the same GuildNavbar in both — so the entry sits in the
	// same slot above the channel list either way, and its transitionTo is the same mechanism a
	// channel tap uses. GuildLayout recognises the resulting route via showsGuildContent, which is
	// route-based and so does not care which control triggered the navigation.
	const canViewCast = ((Permission.getGuildPermissions(guild.id) ?? 0n) & MEMBERS_PAGE_PERMISSIONS) !== 0n;
	// Shared with GuildLayout so the sidebar's idea of "a guild page is open" cannot drift from the
	// layout's — they disagreeing is exactly what left these routes rendering nothing on mobile.
	const guildPageSegment = asGuildPageSegment(getGuildPathSegment(location.pathname, guild.id));
	const selectedChannelInGuildId = getGuildChannelSegment(location.pathname, guild.id);
	const isCastSelected = guildPageSegment === 'cast';
	// Members is no longer linked from this sidebar, but the route still exists and stays reachable
	// from the guild menu, so it must still suppress channel selection rather than read as an id.
	const isOnNonChannelRoute = guildPageSegment != null;
	const handleCastClick = useCallback(() => {
		RouterUtils.transitionTo(Routes.guildCast(guild.id));
	}, [guild.id]);
	const handleCastKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (!isKeyboardActivationKey(event.key)) return;
			event.preventDefault();
			handleCastClick();
		},
		[handleCastClick],
	);
	const collapsedCategories = useMemo(() => {
		const overrides = userGuildSettings?.channel_overrides;
		if (!overrides) return null;
		let collapsed: Set<string> | null = null;
		for (const channelId in overrides) {
			if (overrides[channelId as keyof typeof overrides].collapsed) {
				if (!collapsed) collapsed = new Set<string>();
				collapsed.add(channelId);
			}
		}
		return collapsed;
	}, [userGuildSettings]);
	const toggleCategory = useCallback(
		(categoryId: string) => {
			UserGuildSettingsCommands.toggleChannelCollapsed(guild.id, categoryId);
		},
		[guild.id],
	);
	const channelGroups = useMemo(() => organizeChannels(channels), [channels]);
	const showTrailingDropZone = channelGroups.length > 0;
	const channelIndicatorDependencies = useMemo(
		() => [channels.length, ReadStates.version, userGuildSettings, hideMutedChannels, showFadedUnreadOnMutedChannels],
		[channels.length, ReadStates.version, userGuildSettings, hideMutedChannels, showFadedUnreadOnMutedChannels],
	);
	const getChannelScrollContainer = useCallback(() => scrollerRef.current?.getViewportElement() ?? null, [scrollerRef]);
	useDragAutoScroll({active: isDraggingAnything, getScrollElement: getChannelScrollContainer});
	const handleChannelDrop = useCallback(
		(item: DragItem, result: DropResult) => {
			if (!result) return;
			const guildChannels = Channels.getGuildChannels(guild.id);
			const operation = createChannelMoveOperation({
				channels: guildChannels,
				dragItem: item,
				dropResult: result,
			});
			if (!operation) return;
			void (async () => {
				try {
					await GuildCommands.moveChannel(guild.id, operation);
				} catch (error) {
					if (failureCode(error) === APIErrorCodes.MAX_CATEGORY_CHANNELS) {
						ModalCommands.push(
							ModalCommands.modal(() => (
								<GenericErrorModal
									title={i18n._(CATEGORY_FULL_DESCRIPTOR)}
									message={i18n._(THIS_CATEGORY_ALREADY_CONTAINS_THE_MAXIMUM_OF_CHANNELS_DESCRIPTOR, {
										maxChannelsPerCategory: MAX_CHANNELS_PER_CATEGORY,
									})}
									data-flx="app.channel-list-content.handle-channel-drop.confirm-modal"
								/>
							)),
						);
						return;
					}
					throw error;
				}
			})();
		},
		[guild.id],
	);
	const handleScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			const scrollTop = event.currentTarget.scrollTop;
			const scrollHeight = event.currentTarget.scrollHeight;
			const offsetHeight = event.currentTarget.offsetHeight;
			stickToBottomRef.current = scrollHeight - (scrollTop + offsetHeight) <= 8;
			scrollY.set(scrollTop);
			pendingScrollTopRef.current = scrollTop;
			if (scrollPersistRafRef.current != null) return;
			scrollPersistRafRef.current = requestAnimationFrame(() => {
				scrollPersistRafRef.current = null;
				const pendingScrollTop = pendingScrollTopRef.current;
				if (pendingScrollTop == null) return;
				DimensionCommands.updateChannelListScroll(guild.id, pendingScrollTop);
			});
		},
		[scrollY, guild.id],
	);
	useEffect(() => {
		return () => {
			if (scrollPersistRafRef.current != null) {
				cancelAnimationFrame(scrollPersistRafRef.current);
				scrollPersistRafRef.current = null;
			}
		};
	}, [guild.id]);
	const handleResize = useCallback((_entry: ResizeObserverEntry, _type: 'container' | 'content') => {
		if (stickToBottomRef.current && scrollerRef.current) {
			scrollerRef.current.jumpToEndEdge({animate: false});
		}
	}, []);
	useLayoutEffect(() => {
		const guildDimensions = Dimension.guildDimensionsFor(guild.id);
		if (guildDimensions.scrollTo) {
			const element = document.querySelector(`[data-channel-id="${guildDimensions.scrollTo}"]`);
			if (element && scrollerRef.current) {
				scrollerRef.current.revealElement({node: element as HTMLElement, preferStartEdge: false});
			}
			DimensionCommands.clearChannelListScrollTo(guild.id);
		} else if (guildDimensions.scrollTop && guildDimensions.scrollTop > 0 && scrollerRef.current) {
			scrollerRef.current.scrollTo({to: guildDimensions.scrollTop, animate: false});
		}
		scrollY.set(scrollerRef.current?.getViewportElement()?.scrollTop ?? 0);
	}, [guild.id, scrollY]);
	const handleContextMenu = useCallback(
		(event: React.MouseEvent) => {
			ContextMenuCommands.openFromEvent(event, ({onClose}) => (
				<ChannelListContextMenu
					guild={guild}
					onClose={onClose}
					data-flx="app.channel-list-content.handle-context-menu.channel-list-context-menu"
				/>
			));
		},
		[guild],
	);
	const getUnreadStateInChannel = (channelId: string): ChannelUnreadState => {
		const unreadCount = ReadStates.getUnreadCount(channelId);
		const hasUnread = ReadStates.hasUnread(channelId);
		const mentionCount = ReadStates.getMentionCount(channelId);
		const isMuted = UserGuildSettings.isChannelDirectlyMuted(guild.id, channelId);
		const channel = Channels.getChannel(channelId);
		const unreadBadgesLevel = channel
			? UserGuildSettings.resolvedUnreadBadgesLevel({
					id: channel.id,
					guildId: channel.guildId ?? undefined,
					parentId: channel.parentId ?? undefined,
					type: channel.type,
				})
			: null;
		return getChannelUnreadState({
			hasUnread,
			unreadCount,
			mentionCount,
			isMuted,
			showFadedUnreadOnMutedChannels,
			unreadBadgesLevel,
		});
	};
	/** A channel row plus whether the category it actually lives in counts as muted. */
	type SurfacedChannel = {channel: Channel; categoryMuted: boolean};
	/**
	 * Every channel anywhere beneath a collapsed category.
	 *
	 * `categoryMuted` is carried per channel rather than taken from the collapsed category, because a
	 * channel three levels down belongs to its own category's mute state — and a mute anywhere up the
	 * chain still silences everything under it, which is why it propagates downward here.
	 */
	const collectSubtreeChannels = (
		group: ChannelGroup,
	): {text: Array<SurfacedChannel>; voice: Array<SurfacedChannel>} => {
		const text: Array<SurfacedChannel> = [];
		const voice: Array<SurfacedChannel> = [];
		const walk = (current: ChannelGroup, ancestorMuted: boolean): void => {
			const categoryMuted =
				ancestorMuted ||
				(current.category ? UserGuildSettings.isChannelDirectlyMuted(guild.id, current.category.id) : false);
			// Tree order (the one interleaved order), split into the text and voice lists the collapsed view shows.
			for (const item of current.items) {
				if (item.kind === 'category') {
					walk(item.group, categoryMuted);
				} else if (isVoiceChannel(item.channel)) {
					voice.push({channel: item.channel, categoryMuted});
				} else {
					text.push({channel: item.channel, categoryMuted});
				}
			}
		};
		walk(group, false);
		return {text, voice};
	};
	const keepWhenHidingMuted = (entry: SurfacedChannel, isVoice: boolean): boolean => {
		const isChannelMuted = UserGuildSettings.isChannelDirectlyMuted(guild.id, entry.channel.id);
		const isMuted = entry.categoryMuted || isChannelMuted;
		return shouldShowChannelWhenHidingMutedChannels({
			isCategoryMuted: entry.categoryMuted,
			isChannelMuted,
			isSelected: entry.channel.id === selectedChannelInGuildId,
			isConnected: isVoice && entry.channel.id === connectedChannelId,
			hasVisibleUnread: isMuted && getUnreadStateInChannel(entry.channel.id).hasVisibleUnread,
		});
	};
	const renderTextRow = (entry: SurfacedChannel, isCollapsed: boolean): React.ReactNode => (
		<React.Fragment key={entry.channel.id}>
			<ChannelItem
				guild={guild}
				channel={entry.channel}
				isDraggingAnything={isDraggingAnything}
				activeDragItem={activeDragItem}
				onChannelDrop={handleChannelDrop}
				onDragStateChange={setActiveDragItem}
				isSelectedByPath={selectedChannelInGuildId === entry.channel.id}
				isOnNonChannelRoute={isOnNonChannelRoute}
				data-flx="app.channel-list-content.channel-item--2"
			/>
			{/* As upstream: under a collapsed category only the selected thread stays visible. */}
			{threadsActive && (
				<SidebarThreadList
					guildId={guild.id}
					parentId={entry.channel.id}
					onlySelected={isCollapsed}
					data-flx="app.channel-list-content.sidebar-thread-list"
				/>
			)}
		</React.Fragment>
	);
	const renderVoiceRow = (entry: SurfacedChannel, isCollapsed: boolean): React.ReactNode => {
		const ch = entry.channel;
		const channelRow = (
			<ChannelItem
				key={ch.id}
				guild={guild}
				channel={ch}
				isDraggingAnything={isDraggingAnything}
				activeDragItem={activeDragItem}
				onChannelDrop={handleChannelDrop}
				onDragStateChange={setActiveDragItem}
				isSelectedByPath={selectedChannelInGuildId === ch.id}
				isOnNonChannelRoute={isOnNonChannelRoute}
				data-flx="app.channel-list-content.channel-item--3"
			/>
		);
		if (isCollapsed && connectedChannelId && ch.id === connectedChannelId) {
			return (
				<React.Fragment key={ch.id}>
					{channelRow}
					<CollapsedChannelAvatarStack
						guild={guild}
						channel={ch}
						data-flx="app.channel-list-content.collapsed-channel-avatar-stack"
					/>
				</React.Fragment>
			);
		}
		return (
			<React.Fragment key={ch.id}>
				{channelRow}
				{!isCollapsed && (
					<VoiceParticipantsList
						guild={guild}
						channel={ch}
						data-flx="app.channel-list-content.voice-participants-list"
					/>
				)}
			</React.Fragment>
		);
	};
	/**
	 * Renders one category and everything under it, recursing for nested categories.
	 *
	 * LOCAL-ONLY: expanded, its channels and nested categories render interleaved, in the one order the shared tree
	 * builder gives (nested-ordering-plan.md 6.1), so a subcategory can sit between two channels and a voice
	 * channel above a text one. A voice row keeps its participants right under it, a text row its threads.
	 *
	 * Collapsing hides the entire subtree — no descendant category header, no descendant channel row —
	 * except for the rows the sidebar has always refused to hide: the selected channel, unread ones,
	 * and the voice channel you are connected to. Those surface from ANY depth, so collapsing a
	 * top-level category cannot silently swallow a mention buried three levels down. LOCAL-ONLY: the surfaced
	 * rows show in sidebar (tree) order, text and voice mixed, like the expanded view.
	 *
	 * A descendant's own collapsed flag is never written here, only read, so a child that was collapsed
	 * before its parent was collapsed comes back collapsed when the parent expands.
	 */
	const renderGroup = (
		group: ChannelGroup,
		nested: boolean,
		skeletonGroups: Array<ResolvedChannelGroup>,
	): React.ReactNode => {
		const category = group.category;
		const isNullSpace = !category;
		const firstItem = group.items[0];
		const groupKey =
			category?.id ?? (firstItem?.kind === 'channel' ? `null-space-${firstItem.channel.id}` : 'null-space');
		const isCollapsed = category ? (collapsedCategories?.has(category.id) ?? false) : false;
		const isCategoryMuted = category ? UserGuildSettings.isChannelDirectlyMuted(guild.id, category.id) : false;
		// Collapsed: the candidate pool is the whole subtree. Expanded: only this category's own
		// channels, because its nested categories render themselves.
		const ownChannels = groupChannels(group).map((channel) => ({channel, categoryMuted: isCategoryMuted}));
		const pool = isCollapsed
			? collectSubtreeChannels(group)
			: {
					text: ownChannels.filter((entry) => !isVoiceChannel(entry.channel)),
					voice: ownChannels.filter((entry) => isVoiceChannel(entry.channel)),
				};
		const filteredTextChannels = hideMutedChannels
			? pool.text.filter((entry) => keepWhenHidingMuted(entry, false))
			: pool.text;
		const filteredVoiceChannels = hideMutedChannels
			? pool.voice.filter((entry) => keepWhenHidingMuted(entry, true))
			: pool.voice;
		let visibleTextChannels: ReadonlyArray<SurfacedChannel> = filteredTextChannels;
		let visibleVoiceChannels: ReadonlyArray<SurfacedChannel> = filteredVoiceChannels;
		let connectedChannelInGroup = false;
		if (isCollapsed) {
			visibleTextChannels = filteredTextChannels.filter((entry) => {
				const unreadState = getUnreadStateInChannel(entry.channel.id);
				return shouldShowChannelInCollapsedCategory({
					isCategoryMuted: entry.categoryMuted,
					isSelected: entry.channel.id === selectedChannelInGuildId,
					isConnected: false,
					hasVisibleUnread: unreadState.hasVisibleUnread,
					hasMentions: unreadState.hasMentions,
				});
			});
			const voiceSet = new Set<string>();
			if (selectedChannelInGuildId) voiceSet.add(selectedChannelInGuildId);
			if (connectedChannelId) {
				for (const entry of filteredVoiceChannels) {
					if (entry.channel.id === connectedChannelId) {
						connectedChannelInGroup = true;
						voiceSet.add(connectedChannelId);
						break;
					}
				}
			}
			for (const entry of filteredVoiceChannels) {
				const unreadState = getUnreadStateInChannel(entry.channel.id);
				if (
					shouldShowChannelInCollapsedCategory({
						isCategoryMuted: entry.categoryMuted,
						isSelected: entry.channel.id === selectedChannelInGuildId,
						isConnected: entry.channel.id === connectedChannelId,
						hasVisibleUnread: unreadState.hasVisibleUnread,
						hasMentions: unreadState.hasMentions,
					})
				) {
					voiceSet.add(entry.channel.id);
				}
			}
			if (voiceSet.size === 0) {
				visibleVoiceChannels = EMPTY_ARRAY;
			} else {
				const next: Array<SurfacedChannel> = [];
				let connectedRow: SurfacedChannel | null = null;
				for (const entry of filteredVoiceChannels) {
					if (!voiceSet.has(entry.channel.id)) continue;
					if (entry.channel.id === connectedChannelId) {
						connectedRow = entry;
					} else {
						next.push(entry);
					}
				}
				visibleVoiceChannels = connectedRow ? [connectedRow, ...next] : next;
			}
		}
		// The body in DOM order. Children report into their own skeleton lists, which follow this group's entry;
		// rows after a nested category open a header-less continuation entry, so the memory sees the DOM order.
		const body: Array<React.ReactNode> = [];
		const headerRows: Array<Channel> = [];
		const followingSkeletonGroups: Array<ResolvedChannelGroup> = [];
		let hasVisibleChildren = false;
		if (isCollapsed) {
			const surfaced = inTreeOrder(subtreeChannelOrder(group), [...visibleTextChannels, ...visibleVoiceChannels]);
			for (const entry of surfaced) {
				body.push(isVoiceChannel(entry.channel) ? renderVoiceRow(entry, true) : renderTextRow(entry, true));
				headerRows.push(entry.channel);
			}
		} else {
			const shown = new Set<string>();
			for (const entry of filteredTextChannels) shown.add(entry.channel.id);
			for (const entry of filteredVoiceChannels) shown.add(entry.channel.id);
			let segmentRows: Array<Channel> = headerRows;
			let continuation: ResolvedChannelGroup | null = null;
			let continuationIndex = 0;
			for (const item of group.items) {
				if (item.kind === 'category') {
					const node = renderGroup(item.group, true, followingSkeletonGroups);
					if (node != null) {
						hasVisibleChildren = true;
						body.push(node);
						continuation = null;
					}
					continue;
				}
				if (!shown.has(item.channel.id)) continue;
				const entry: SurfacedChannel = {channel: item.channel, categoryMuted: isCategoryMuted};
				body.push(isVoiceChannel(item.channel) ? renderVoiceRow(entry, false) : renderTextRow(entry, false));
				if (followingSkeletonGroups.length > 0 && continuation === null) {
					continuationIndex += 1;
					segmentRows = [];
					continuation = {
						key: `${groupKey}:after-${continuationIndex}`,
						category: undefined,
						isCollapsed: false,
						rows: segmentRows,
					};
					followingSkeletonGroups.push(continuation);
				}
				segmentRows.push(item.channel);
			}
		}
		if (isNullSpace && filteredTextChannels.length === 0 && filteredVoiceChannels.length === 0) {
			return null;
		}
		// A category whose own channels are all hidden still renders when a nested category under it
		// has something to show — dropping it would orphan that descendant.
		if (
			hideMutedChannels &&
			category &&
			!shouldShowCategoryWhenHidingMutedChannels({
				// Nested categories count as content: an expanded category whose only children are
				// categories is not "empty", and it stays only while one of them has something to show.
				hasChannels: pool.text.length > 0 || pool.voice.length > 0 || (!isCollapsed && groupChildren(group).length > 0),
				hasVisibleChannels: filteredTextChannels.length > 0 || filteredVoiceChannels.length > 0 || hasVisibleChildren,
			})
		) {
			return null;
		}
		skeletonGroups.push({key: groupKey, category, isCollapsed, rows: headerRows}, ...followingSkeletonGroups);
		return (
			<div
				key={groupKey}
				className={clsx(styles.channelGroup, nested && styles.nestedChannelGroup)}
				data-flx="app.channel-list-content.channel-group"
			>
				{category && (
					<ChannelItem
						guild={guild}
						channel={category}
						isCollapsed={isCollapsed}
						onToggle={() => toggleCategory(category.id)}
						isDraggingAnything={isDraggingAnything}
						activeDragItem={activeDragItem}
						onChannelDrop={handleChannelDrop}
						onDragStateChange={setActiveDragItem}
						isSelectedByPath={selectedChannelInGuildId === category.id}
						isOnNonChannelRoute={isOnNonChannelRoute}
						data-flx="app.channel-list-content.channel-item"
					/>
				)}
				{isCollapsed && category && !connectedChannelInGroup && (
					<CollapsedCategoryVoiceParticipants
						guild={guild}
						voiceChannels={filteredVoiceChannels.map((entry) => entry.channel)}
						data-flx="app.channel-list-content.collapsed-category-voice-participants"
					/>
				)}
				{body}
			</div>
		);
	};
	// Upstream's skeleton-layout memory measures category and channel names in DOM order and pairs them
	// with a flat group list. The sidebar renders nested categories recursively, so `renderGroup` reports
	// a depth-first list in exactly that render order (rows and nested categories interleaved, rows after a
	// nested category as a continuation entry). The skeleton draws nested categories without indentation; a
	// depth-aware skeleton is a post-sync follow-up.
	const resolvedGroups: Array<ResolvedChannelGroup> = [];
	const channelGroupNodes = channelGroups.map((group) => renderGroup(group, false, resolvedGroups));
	useSkeletonLayoutReport(
		() => {
			reportSkeletonGuildChannelList(guild.id, {
				// The Cast row replaced upstream's Members row in the same slot (same permission gate).
				membersRowVisible: canViewCast,
				groups: createRememberedChannelGroups(resolvedGroups, channelGroupsContainerRef.current),
			});
		},
		`${guild.id}|${createChannelListProjectionKey(resolvedGroups, canViewCast)}`,
	);
	return (
		<div
			className={styles.channelListScrollerWrapper}
			data-flx="app.channel-list-content.channel-list-scroller-wrapper"
		>
			<Scroller
				ref={scrollerRef}
				className={clsx(styles.channelListScroller, showIntegratedBanner && styles.channelListScrollerOverBanner)}
				onScroll={handleScroll}
				onResize={handleResize}
				key={guild.id}
				data-flx="app.channel-list-content.channel-list-scroller"
			>
				<div
					className={styles.navigationContainer}
					onContextMenu={handleContextMenu}
					role="navigation"
					aria-label={i18n._(CHANNELS_DESCRIPTOR, {guildName: guild.name})}
					ref={channelListNavigationRef}
					data-flx="app.channel-list-content.navigation-container.context-menu"
				>
					{showIntegratedBanner && (
						<div
							ref={banner.hoverRef}
							className={styles.bannerSpacer}
							style={{height: banner.collapseDistance}}
							data-flx="app.channel-list-content.banner-spacer"
						/>
					)}
					<GuildDetachedBanner guild={guild} data-flx="app.channel-list-content.guild-detached-banner" />
					<div className={styles.topDropZone} data-flx="app.channel-list-content.top-drop-zone">
						<NullSpaceDropIndicator
							isDraggingAnything={isDraggingAnything}
							onChannelDrop={handleChannelDrop}
							variant="top"
							data-flx="app.channel-list-content.null-space-drop-indicator"
						/>
					</div>
					{canViewCast && (
						<>
							<div className={styles.membersSection} data-flx="app.channel-list-content.cast-section">
								<GenericChannelItem
									containerClassName={channelItemStyles.container}
									className={clsx(
										channelItemStyles.channelItem,
										channelItemStyles.channelItemRegular,
										isCastSelected && channelItemStyles.channelItemSelected,
										!isCastSelected && channelItemStyles.channelItemHoverable,
									)}
									isSelected={isCastSelected}
									aria-label={isCastSelected ? i18n._(CAST_SELECTED_DESCRIPTOR) : i18n._(CAST_DESCRIPTOR)}
									aria-current={isCastSelected ? 'page' : undefined}
									onClick={handleCastClick}
									onKeyDown={handleCastKeyDown}
									data-flx="app.channel-list-content.generic-channel-item.cast-click"
								>
									<ChannelItemContent
										icon={
											<UsersThreeIcon
												size={remFromPx(20)}
												className={clsx(
													channelItemStyles.channelItemIcon,
													isCastSelected
														? channelItemStyles.channelItemIconSelected
														: channelItemStyles.channelItemIconUnselected,
												)}
												data-flx="app.channel-list-content.cast-icon"
											/>
										}
										name={i18n._(CAST_DESCRIPTOR)}
										data-flx="app.channel-list-content.channel-item-content"
									/>
								</GenericChannelItem>
							</div>
							<div className={styles.membersSeparator} data-flx="app.channel-list-content.cast-separator" />
						</>
					)}
					<div
						ref={channelGroupsContainerRef}
						className={styles.channelGroupsContainer}
						data-flx="app.channel-list-content.channel-groups-container"
					>
						{channelGroupNodes}
					</div>
					{showTrailingDropZone && (
						<div className={styles.bottomDropZone} data-flx="app.channel-list-content.bottom-drop-zone">
							<NullSpaceDropIndicator
								isDraggingAnything={isDraggingAnything}
								onChannelDrop={handleChannelDrop}
								variant="bottom"
								data-flx="app.channel-list-content.null-space-drop-indicator--2"
							/>
						</div>
					)}
					<div className={styles.bottomSpacer} data-flx="app.channel-list-content.bottom-spacer" />
				</div>
			</Scroller>
			<ScrollIndicatorOverlay
				getScrollContainer={getChannelScrollContainer}
				scrollContainerIdentity={guild.id}
				dependencies={channelIndicatorDependencies}
				label={i18n._(NEW_MESSAGES_DESCRIPTOR)}
				data-flx="app.channel-list-content.scroll-indicator-overlay"
			/>
		</div>
	);
});
